import './index.css';

import {div, span} from '../../src/utils/html.ts';
import type {ProductData, StarredResource} from '../../types/types.ts';
import {renderApiError} from '../components/error.ts';
import {render as resourceStar} from '../components/wc/resource-star.ts';
import {isAuthenticated} from '../lib/auth.ts';
import {createElement} from '../lib/dom';
import {clearAuthToken, clearClientId, getClientId} from '../lib/storage.ts';
import {Store} from '../lib/store.ts';
import {loadContentSources, loadMagazines, loadPrintProducts} from '../services/content';
import {renderContentSources} from './content-sources.ts';

const mainPrintProductNames = [
	'AVN_AVI',
	'ABB_AVI',
	'VEA_AVI',
	'TBL_AVI',
	'JAB_AVI',
	'NNS-Z1-E1',
	'NAA-Z1-E1',
	'N3SE-Z1-E1',
	'N3SW-Z1-E1',
	'N3SF-Z1-E1',
	'dn-dn-print-automation',
	'APHA',
	'APAE'
];

export async function renderIndexPage(appEl: HTMLElement) {
	appEl.innerHTML = `
<div class="starred-resources"></div>
<div class="products"></div>
<div class="digital">
	<div class="feeds"></div>
	<div class="reels"></div>
	<div class="magazines"></div>
</div>
`;
	const starredEl = appEl.querySelector('.starred-resources') as HTMLElement;
	const productsEl = appEl.querySelector('.products') as HTMLElement;
	const feedsEl = appEl.querySelector('.feeds') as HTMLElement;
	const reelsEl = appEl.querySelector('.reels') as HTMLElement;
	const magazinesEl = appEl.querySelector('.magazines') as HTMLElement;

	renderLogoutLink();
	await renderContent();

	function renderLogoutLink() {
		if (isAuthenticated()) {
			const logoutEl = createElement(appEl.ownerDocument, 'a', 'logout');
			logoutEl.innerHTML = 'Log out';
			logoutEl.addEventListener('click', (event) => {
				event.preventDefault();
				clearAuthToken();
				clearClientId();
				window.location.href = '/logout';
			});
			appEl.appendChild(logoutEl);
		}
	}

	async function renderContent() {
		const store = await Store.load();
		productsEl.innerHTML = 'Loading products';
		const productsById: Record<string, ProductData> = {};
		const magazinesById: Record<string, ProductData> = {};

		renderStarredResources();
		await renderPrintProducts();
		await renderFeeds();
		await renderReels();
		await renderMagazines();

		appEl.addEventListener('star:change', () => renderStarredResources());

		function renderStarredResources() {
			const resources = store.getStarredResources();
			const groups: Record<string, StarredResource[]> = {
				feed: [],
				'print-product': [],
				'digital-product': [],
				article: []
			};
			for (const starred of resources) {
				if (!groups[starred.type]) {
					groups[starred.type] = [];
				}
				groups[starred.type].push(starred);
			}

			starredEl.innerHTML = `
			${renderStarredSection(groups.feed, '★ Feeds')}
			${renderStarredSection(groups['print-product'], '★ Print products')}
			${renderStarredSection(groups['digital-product'], '★ Digitial products')}
			<h2>★ Articles</h2>
			${groups.article
				.map((starred) =>
					div(`<a href="${starred.path}">
${span(starred.context, 'article-context')}
${span(starred.label, 'article-title')}
</a>`)
				)
				.join('\n')}
			`;
		}

		function renderStarredSection(items: StarredResource[], header: string) {
			if (!items.length) {
				return '';
			}
			return `
			<h2>${header}</h2>
			${items
				.toSorted(sortByLabel)
				.map((starred) => `<a href="${starred.path}">${starred.label}</a>`)
				.join('\n')}`;
		}

		function sortByLabel(a: StarredResource, b: StarredResource) {
			return a.label.localeCompare(b.label);
		}

		async function renderPrintProducts() {
			const response = await loadPrintProducts();
			if (response.status !== 200) {
				productsEl.innerHTML = renderApiError(response.body, 'Error loading products');
				return;
			}

			productsEl.innerHTML = '';

			const mainProducts: ProductData[] = [];

			response.body.forEach((product) => {
				productsById[product.id] = product;
				if (mainPrintProductNames.includes(product.attributes.name)) {
					mainProducts.push(product);
				}
			});

			if (mainProducts.length) {
				productsEl.innerHTML = `
			<h2>Main products</h2>
${mainProducts
	.sort((a, b) => a.attributes.title.localeCompare(b.attributes.title, 'no'))
	.map(
		({id, attributes: {title, name}}) => `<div class="product"><a href="/print-products/${name}">${title}</a>
${resourceStar({
	type: 'print-product',
	id,
	label: title,
	path: `/print-products/${name}`
})}</div>`
	)
	.join('\n')}
`;
			}

			productsEl.innerHTML += `
			<h2>Print products</h2>
		 ${response.body
				.sort((a, b) => a.attributes.title.localeCompare(b.attributes.title, 'no'))
				.map(
					({id, attributes: {title, name}}) =>
						`<div class="product"><a href="/print-products/${name}">${title}</a>
	${resourceStar({
		type: 'print-product',
		id,
		label: title,
		path: `/print-products/${name}`
	})}
</div>`
				)
				.join('\n')}
		  `;
		}

		async function renderMagazines() {
			const response = await loadMagazines();
			if (response.status !== 200) {
				magazinesEl.innerHTML = renderApiError(response.body, 'Error loading magazine products');
				return;
			}

			if (response.body.length === 0) {
				return;
			}

			response.body.forEach((mag) => {
				magazinesById[mag.id] = mag;
			});

			magazinesEl.innerHTML = `
			<h2>Digital products</h2>
		 ${response.body
				.sort((a, b) => a.attributes.title.localeCompare(b.attributes.title, 'no'))
				.map(
					({attributes: {title, name}}) =>
						`<div class="magazine"><a href="/digital-products/${name}">${title}</a></div>`
				)
				.join('\n')}
		  `;
		}

		async function renderReels() {
			if (getClientId() !== 'nordjyske') {
				return;
			}

			reelsEl.innerHTML = `
<h2>Reels</h2>
<div class="reels-edition"><a href="/reels-edition/z9qYRYGx">Reels</a></div>
`;
		}

		async function renderFeeds() {
			const response = await loadContentSources();
			if (response.status !== 200) {
				feedsEl.innerHTML = renderApiError(response.body, 'Error loading feeds');
				return;
			}

			if (response.body.length === 0) {
				return;
			}

			feedsEl.innerHTML = '<h2>Feeds</h2>';
			renderContentSources(feedsEl, response.body);
		}
	}
}
